import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead, { PAGE_SEO } from "@/components/SEOHead";
import { HeaderStickyBanner } from "@/components/AdPlacements";
import {
  ChatCircle,
  Users,
  Cpu,
  Book,
  Building,
  ShoppingCart,
  Eye,
  Heart,
  HeartStraight,
  Clock,
  Plus,
  X,
  CircleNotch,
  CaretRight,
  PaperPlaneRight,
  Globe,
  MapPin,
  MagnifyingGlass,
  Newspaper,
  Trash
} from "@phosphor-icons/react";
import { toast } from "sonner";

// Import refactored community components
import { NewsArticleCard } from "@/components/community/NewsArticleCard";
import { TopicDetailModal } from "@/components/community/TopicDetailModal";
import { CreateTopicModal } from "@/components/community/CreateTopicModal";
import { TopicCard } from "@/components/community/TopicCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  "technology": Cpu,
  "best-practices": Book,
  "government": Building,
  "networking": Users,
  "marketplace": ShoppingCart,
  "news": Newspaper,
  "general": ChatCircle
};

const REGIONS = [
  { id: "global", name: "Global", icon: Globe },
  { id: "ireland", name: "Ireland", icon: MapPin },
  { id: "uk", name: "UK", icon: MapPin },
  { id: "usa", name: "USA", icon: MapPin },
  { id: "eu", name: "Europe", icon: MapPin }
];

export default function CommunityPage() {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Data state
  const [topics, setTopics] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({ total_topics: 0, total_replies: 0, active_users: 0 });
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || null);
  const [selectedRegion, setSelectedRegion] = useState(searchParams.get("region") || null);
  const [searchQuery, setSearchQuery] = useState("");
  
  // UI state
  const [showNewTopic, setShowNewTopic] = useState(false);
  const [showTopicDetail, setShowTopicDetail] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // New topic form
  const [newTopic, setNewTopic] = useState({ title: "", content: "", category: "general", region: "global" });
  const [newReply, setNewReply] = useState("");
  
  // News discussion feature
  const [recentArticles, setRecentArticles] = useState([]);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [articleComments, setArticleComments] = useState([]);
  const [newArticleComment, setNewArticleComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);

  // Data fetching
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      if (selectedRegion) params.region = selectedRegion;

      const [topicsRes, categoriesRes, statsRes, articlesRes] = await Promise.all([
        axios.get(`${API}/community/topics`, { params }),
        axios.get(`${API}/community/categories`),
        axios.get(`${API}/community/stats`),
        axios.get(`${API}/articles`, { params: { limit: 5 } })
      ]);

      setTopics(topicsRes.data.topics);
      setCategories(categoriesRes.data.categories);
      setStats(statsRes.data);
      setRecentArticles(articlesRes.data);
    } catch { /* Silent fail */ }
    finally { setLoading(false); }
  }, [selectedCategory, selectedRegion]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchTopicDetail = async (topicId) => {
    try {
      const response = await axios.get(`${API}/community/topics/${topicId}`);
      setShowTopicDetail(response.data);
    } catch { toast.error("Failed to load topic"); }
  };

  const fetchArticleComments = async (articleId) => {
    setLoadingComments(true);
    try {
      const response = await axios.get(`${API}/community/article-comments/${articleId}`);
      setArticleComments(response.data.comments || []);
    } catch { setArticleComments([]); }
    finally { setLoadingComments(false); }
  };

  // Action handlers
  const handleCreateTopic = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please sign in to create a topic"); return; }
    if (!newTopic.title.trim() || !newTopic.content.trim()) { toast.error("Please fill in all fields"); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/community/topics`, newTopic, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Topic created!");
      setShowNewTopic(false);
      setNewTopic({ title: "", content: "", category: "general", region: "global" });
      fetchData();
    } catch { toast.error("Failed to create topic"); }
    finally { setSubmitting(false); }
  };

  const handleReply = async (topicId) => {
    if (!user) { toast.error("Please sign in to reply"); return; }
    if (!newReply.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/community/topics/${topicId}/reply`, { content: newReply }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Reply posted!");
      setNewReply("");
      fetchTopicDetail(topicId);
    } catch { toast.error("Failed to post reply"); }
    finally { setSubmitting(false); }
  };

  const handleLike = async (topicId) => {
    if (!user) { toast.error("Please sign in to like"); return; }
    try {
      await axios.post(`${API}/community/topics/${topicId}/like`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
      if (showTopicDetail?.id === topicId) fetchTopicDetail(topicId);
    } catch { toast.error("Failed to like topic"); }
  };

  const handleDeleteArticleComment = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;
    try {
      await axios.delete(`${API}/community/article-comments/${commentId}`, { headers: { Authorization: `Bearer ${token}` } });
      setArticleComments(articleComments.filter(c => c.id !== commentId));
      toast.success("Comment deleted");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete comment"); }
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm("Are you sure you want to delete this topic and all its replies?")) return;
    try {
      await axios.delete(`${API}/community/topics/${topicId}`, { headers: { Authorization: `Bearer ${token}` } });
      setTopics(topics.filter(t => t.id !== topicId));
      if (showTopicDetail?.id === topicId) setShowTopicDetail(null);
      toast.success("Topic deleted");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete topic"); }
  };

  const handleDeleteReply = async (topicId, replyId) => {
    if (!window.confirm("Are you sure you want to delete this reply?")) return;
    try {
      await axios.delete(`${API}/community/topics/${topicId}/replies/${replyId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchTopicDetail(topicId);
      toast.success("Reply deleted");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete reply"); }
  };

  const handleAddArticleComment = async () => {
    if (!user) { toast.error("Please sign in to comment"); return; }
    if (!newArticleComment.trim() || !expandedArticle) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/community/article-comments`, { article_id: expandedArticle, content: newArticleComment }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Comment posted!");
      setNewArticleComment("");
      fetchArticleComments(expandedArticle);
    } catch { toast.error("Failed to post comment"); }
    finally { setSubmitting(false); }
  };

  const toggleArticleExpansion = (articleId) => {
    if (expandedArticle === articleId) {
      setExpandedArticle(null);
      setArticleComments([]);
    } else {
      setExpandedArticle(articleId);
      fetchArticleComments(articleId);
    }
    setNewArticleComment("");
  };

  // Utility
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const filteredTopics = topics.filter(t =>
    t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <SEOHead {...PAGE_SEO.community} url="/community" />
      <HeaderStickyBanner />
      <Header />

      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <PageHeader topicsCount={topics.length} />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <CategoriesSidebar
              categories={categories}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              topicsCount={topics.length}
            />
            <RegionsSidebar
              selectedRegion={selectedRegion}
              setSelectedRegion={setSelectedRegion}
            />
            <StatsSidebar stats={stats} />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* News Discussion Section */}
            <NewsDiscussionSection
              recentArticles={recentArticles}
              expandedArticle={expandedArticle}
              articleComments={articleComments}
              loadingComments={loadingComments}
              newArticleComment={newArticleComment}
              setNewArticleComment={setNewArticleComment}
              submitting={submitting}
              onToggle={toggleArticleExpansion}
              onAddComment={handleAddArticleComment}
              onDeleteComment={handleDeleteArticleComment}
              user={user}
              formatDate={formatDate}
            />


            {/* Forum Topics Header */}
            <ForumTopicsHeader
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              user={user}
              onNewTopic={() => setShowNewTopic(true)}
            />

            {/* Topics List */}
            <TopicsList
              loading={loading}
              filteredTopics={filteredTopics}
              user={user}
              onTopicClick={(topic) => { fetchTopicDetail(topic.id); }}
              onDeleteTopic={handleDeleteTopic}
              formatDate={formatDate}
            />
          </div>
        </div>
      </main>

      {/* New Topic Modal - Using Refactored Component */}
      <CreateTopicModal
        show={showNewTopic}
        onClose={() => setShowNewTopic(false)}
        newTopic={newTopic}
        setNewTopic={setNewTopic}
        categories={categories}
        regions={REGIONS}
        onSubmit={handleCreateTopic}
        submitting={submitting}
      />

      {/* Topic Detail Modal - Using Refactored Component */}
      {showTopicDetail && (
        <TopicDetailModal
          topic={showTopicDetail}
          onClose={() => setShowTopicDetail(null)}
          replies={showTopicDetail.replies || []}
          loadingReplies={false}
          newReply={newReply}
          onReplyChange={setNewReply}
          onSubmitReply={() => handleReply(showTopicDetail.id)}
          submitting={submitting}
          onDeleteReply={(replyId) => handleDeleteReply(showTopicDetail.id, replyId)}
          user={user}
          formatDate={formatDate}
        />
      )}

      <Footer />
    </div>
  );
}

// Sub-components for cleaner organization

function PageHeader({ topicsCount }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <span className="px-2 py-1 bg-[#0F766E] text-white text-xs font-mono uppercase tracking-wider">Community</span>
        <span className="px-2 py-1 bg-[#FEF3C7] text-[#92400E] text-xs font-mono uppercase tracking-wider">{topicsCount} Discussions</span>
      </div>
      <h1 className="font-serif text-3xl sm:text-4xl text-[#33302E] mb-2">Farmer Community</h1>
      <p className="text-[#6B6661] font-sans">Connect with farmers worldwide. Share knowledge, discuss technology, and shape agricultural policy.</p>
    </div>
  );
}

function CategoriesSidebar({ categories, selectedCategory, setSelectedCategory, topicsCount }) {
  return (
    <div className="bg-white border border-[#E0D4C8] p-4">
      <h3 className="font-sans font-medium text-[#33302E] mb-3">Categories</h3>
      <div className="space-y-1">
        <button
          onClick={(e) => { e.stopPropagation(); setSelectedCategory(null); }}
          className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${!selectedCategory ? "bg-[#F0FDF4] text-[#0F766E]" : "text-[#6B6661] hover:bg-[#F9FAFB]"}`}
        >
          <span>All Topics</span>
          <span className="text-xs">{topicsCount}</span>
        </button>
        {categories.map((cat) => {
          const Icon = CATEGORY_ICONS[cat.id] || ChatCircle;
          return (
            <button
              key={cat.id}
              onClick={(e) => { e.stopPropagation(); setSelectedCategory(cat.id); }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${selectedCategory === cat.id ? "bg-[#F0FDF4] text-[#0F766E]" : "text-[#6B6661] hover:bg-[#F9FAFB]"}`}
            >
              <span className="flex items-center gap-2"><Icon size={16} />{cat.name}</span>
              <span className="text-xs">{cat.topic_count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RegionsSidebar({ selectedRegion, setSelectedRegion }) {
  return (
    <div className="bg-white border border-[#E0D4C8] p-4">
      <h3 className="font-sans font-medium text-[#33302E] mb-3">Region</h3>
      <div className="space-y-1">
        {REGIONS.map((region) => {
          const Icon = region.icon;
          return (
            <button
              key={region.id}
              onClick={(e) => { e.stopPropagation(); setSelectedRegion(selectedRegion === region.id ? null : region.id); }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${selectedRegion === region.id ? "bg-[#F0FDF4] text-[#0F766E]" : "text-[#6B6661] hover:bg-[#F9FAFB]"}`}
            >
              <Icon size={16} />{region.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StatsSidebar({ stats }) {
  return (
    <div className="bg-[#0F766E] text-white p-4">
      <h3 className="font-sans font-medium mb-3">Community Stats</h3>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between"><span>Discussions</span><span className="font-mono">{stats.total_topics}</span></div>
      </div>
    </div>
  );
}

function NewsDiscussionSection({ recentArticles, expandedArticle, articleComments, loadingComments, newArticleComment, setNewArticleComment, submitting, onToggle, onAddComment, onDeleteComment, user, formatDate }) {
  if (recentArticles.length === 0) return null;
  
  return (
    <div className="bg-white border border-[#E0D4C8]">
      <div className="p-4 border-b border-[#E0D4C8]">
        <h2 className="font-serif text-xl text-[#33302E] flex items-center gap-2">
          <Newspaper size={24} className="text-[#0F766E]" />Discuss Today's News
        </h2>
        <p className="text-sm text-[#6B6661] mt-1">Share your thoughts on the latest agricultural news</p>
      </div>
      <div className="divide-y divide-[#E5E7EB]">
        {recentArticles.slice(0, 3).map((article) => (
          <NewsArticleCard
            key={article.id}
            article={article}
            isExpanded={expandedArticle === article.id}
            comments={expandedArticle === article.id ? articleComments : []}
            loadingComments={loadingComments && expandedArticle === article.id}
            onToggle={() => onToggle(article.id)}
            onAddComment={onAddComment}
            onDeleteComment={onDeleteComment}
            newComment={newArticleComment}
            onCommentChange={setNewArticleComment}
            submitting={submitting}
            user={user}
            formatDate={formatDate}
          />
        ))}
      </div>
    </div>
  );
}

function ForumTopicsHeader({ searchQuery, setSearchQuery, user, onNewTopic }) {
  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div className="relative flex-1 w-full sm:max-w-md">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6661]" />
        <input
          type="text"
          placeholder="Search discussions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-[#E0D4C8] bg-white text-sm focus:outline-none focus:border-[#0F766E]"
        />
      </div>
      {user && (
        <button onClick={onNewTopic} className="flex items-center gap-2 px-4 py-2 bg-[#0F766E] text-white hover:bg-[#0D6B64] transition-colors">
          <Plus size={18} />Start Discussion
        </button>
      )}
    </div>
  );
}

function TopicsList({ loading, filteredTopics, user, onTopicClick, onDeleteTopic, formatDate }) {
  if (loading) {
    return (
      <div className="text-center py-12">
        <CircleNotch size={32} className="animate-spin text-[#0F766E] mx-auto mb-2" />
        <p className="text-[#6B6661]">Loading discussions...</p>
      </div>
    );
  }

  if (filteredTopics.length === 0) {
    return (
      <div className="bg-white border border-[#E0D4C8] p-8 text-center">
        <ChatCircle size={48} className="text-[#D1D5DB] mx-auto mb-4" />
        <p className="text-[#6B6661]">No discussions yet. Start one!</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {filteredTopics.map((topic) => (
        <div
          key={topic.id}
          onClick={() => onTopicClick(topic)}
          className="bg-white border border-[#E0D4C8] p-4 cursor-pointer hover:border-[#0F766E] transition-colors"
          data-testid={`topic-${topic.id}`}
        >
          <div className="flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 bg-[#F6E9DD] text-[#6B6661] text-xs">{topic.category}</span>
                {topic.region !== "global" && <span className="px-2 py-0.5 bg-[#E0F2FE] text-[#0369A1] text-xs">{topic.region}</span>}
                {topic.is_pinned && <span className="px-2 py-0.5 bg-[#FEF3C7] text-[#92400E] text-xs">Pinned</span>}
              </div>
              <h3 className="font-sans font-medium text-[#33302E] mb-1 truncate">{topic.title}</h3>
              <p className="text-sm text-[#6B6661] line-clamp-2">{topic.content}</p>
              <div className="flex items-center gap-4 mt-3 text-xs text-[#6B6661]">
                <span className="flex items-center gap-1"><Clock size={14} />{formatDate(topic.created_at)}</span>
                <span>by {topic.author_name}</span>
                {user && (topic.author_id === user.id || user.role === "admin") && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onDeleteTopic(topic.id); }}
                    className="flex items-center gap-1 px-2 py-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors ml-auto"
                    title="Delete topic"
                  >
                    <Trash size={16} weight="bold" /><span className="text-xs">Delete</span>
                  </button>
                )}
              </div>
            </div>
            <CaretRight size={20} className="text-[#8B8580] flex-shrink-0" />
          </div>
        </div>
      ))}
    </div>
  );
}
